/**
 * Premium Landing Effects — hooks & utils
 *
 * Conjunto de hooks reutilizables que añaden capa de animaciones premium
 * sobre la landing existente SIN modificar su identidad visual:
 *   - useTilt3D         → tilt 3D + spotlight glow siguiendo el cursor
 *   - useMagnetic       → efecto magnético sutil para CTAs
 *   - useMouseParallax  → translación 3D de capa según mouse (Hero bg)
 *   - useScrollZoom     → zoom muy sutil del bg al hacer scroll (Hero)
 *
 * Todos respetan `prefers-reduced-motion`.
 *
 * Nota: El smooth-scroll se hace nativo (CSS `scroll-behavior: smooth`).
 * Lenis fue descartado porque interfería con el scroll touch en móvil.
 */
import { useEffect, useRef } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Stub mantenido por compatibilidad con imports existentes.
// Smooth-scroll real lo hace `html { scroll-behavior: smooth }` en index.css.
export function useLenis() {
  // no-op: smooth scroll nativo, sin librerías que se peleen con touch.
}

// ---------------------------------------------------------------
// useTilt3D — aplica rotación 3D + spotlight a un elemento al pasar mouse
// Usar: const ref = useTilt3D({ max: 8, glow: true }); <div ref={ref} />
// ---------------------------------------------------------------
export function useTilt3D({ max = 8, scale = 1.02, glow = true } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    let rect = el.getBoundingClientRect();
    let raf = 0;

    const updateRect = () => { rect = el.getBoundingClientRect(); };
    updateRect();

    const onEnter = () => {
      updateRect();
      el.style.transition = "transform 0.18s ease-out";
      if (glow) el.classList.add("tilt-glow-on");
    };
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const px = cx / rect.width;   // 0..1
        const py = cy / rect.height;
        const rx = ((py - 0.5) * -2) * max; // rotateX
        const ry = ((px - 0.5) * 2) * max;  // rotateY
        el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`;
        // Spotlight CSS vars (consumidos por .tilt-card::before)
        el.style.setProperty("--spot-x", `${px * 100}%`);
        el.style.setProperty("--spot-y", `${py * 100}%`);
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      el.style.transition = "transform 0.5s cubic-bezier(0.16,1,0.3,1)";
      el.style.transform = "perspective(900px) rotateX(0) rotateY(0) scale(1)";
      if (glow) el.classList.remove("tilt-glow-on");
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", updateRect);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", updateRect);
      cancelAnimationFrame(raf);
    };
  }, [max, scale, glow]);
  return ref;
}

// ---------------------------------------------------------------
// useMagnetic — el elemento se desplaza levemente hacia el cursor.
// Ideal para botones CTA.
// ---------------------------------------------------------------
export function useMagnetic({ strength = 0.25 } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    if (window.matchMedia("(pointer: coarse)").matches) return; // skip mobile

    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = (e.clientX - cx) * strength;
        const dy = (e.clientY - cy) * strength;
        el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      el.style.transition = "transform 0.5s cubic-bezier(0.16,1,0.3,1)";
      el.style.transform = "translate3d(0,0,0)";
      // reset transition tras animar
      setTimeout(() => { if (el) el.style.transition = ""; }, 500);
    };
    const onEnter = () => { el.style.transition = "transform 0.18s ease-out"; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, [strength]);
  return ref;
}

// ---------------------------------------------------------------
// useMouseParallax — el elemento se traslada según el movimiento del mouse
// dentro del viewport. Útil para el background del Hero.
// ---------------------------------------------------------------
export function useMouseParallax({ strength = 18 } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    let raf = 0;
    let tx = 0, ty = 0;
    let targetX = 0, targetY = 0;

    const onMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      targetX = ((e.clientX / w) - 0.5) * -strength;
      targetY = ((e.clientY / h) - 0.5) * -strength;
    };
    const loop = () => {
      tx += (targetX - tx) * 0.06;
      ty += (targetY - ty) * 0.06;
      el.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) scale(1.08)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    loop();
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [strength]);
  return ref;
}

// ---------------------------------------------------------------
// useScrollZoom — aplica un zoom progresivo y muy sutil al hacer scroll
// dentro del rango del elemento (típicamente Hero).
// ---------------------------------------------------------------
export function useScrollZoom({ maxZoom = 0.08 } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    // Skip on mobile/touch to avoid scroll glitches & blur perf hit
    if (window.matchMedia("(pointer: coarse)").matches) return;
    if (window.innerWidth < 1024) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const vh = window.innerHeight || 1;
        const y = window.scrollY;
        // 0 al inicio, 1 al pasar 1 viewport
        const p = Math.min(Math.max(y / vh, 0), 1);
        const zoom = 1 + p * maxZoom;
        const op = 1 - p * 0.12;
        el.style.transform = `scale(${zoom.toFixed(3)})`;
        el.style.opacity = String(op.toFixed(2));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [maxZoom]);
  return ref;
}
